import { Injectable, computed, signal } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { User } from '../features/user/models/user';
import { UserClaims } from '../features/user/models/user-claims';
import { UserService } from '../features/user/services/user.service';
import { LocationStateService } from '../features/locations/services/location-state.service';
import { PermissionCodes } from '../shared/constants/domain.constants';
import { AuthTokenService } from './auth-token.service';

@Injectable({
  providedIn: 'root'
})
export class AuthStateService {
  private readonly userSignal = signal<User | null>(null);
  private readonly claimsSignal = signal<UserClaims | null>(null);
  private readonly loadingSignal = signal(false);
  private readonly errorSignal = signal<string | null>(null);
  private initialized = false;

  readonly user = computed(() => this.userSignal());
  readonly claims = computed(() => this.claimsSignal());
  readonly loading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());

  readonly canAccessSalesOrders = computed(() => {
    const user = this.userSignal();
    if (!user) {
      return false;
    }
    return Number(user.highestPermissionForSalesOrders) !== PermissionCodes.salesOrderNoAccess;
  });

  readonly canAccessCprs = computed(() => {
    const user = this.userSignal();
    if (!user) {
      return false;
    }
    return Number(user.highestPermissionForCPRs) !== PermissionCodes.cprNoAccess;
  });

  readonly canAccessReports = computed(() => {
    const user = this.userSignal();
    return !!user?.isAbleToViewFinancialReports;
  });

  readonly canAccessAdministration = computed(() => {
    const user = this.userSignal();
    return !!user && (user.isCustomerPortalAdmin || user.isLocationAdmin);
  });

  constructor(
    private readonly userService: UserService,
    private readonly locationStateService: LocationStateService,
    private readonly authTokenService: AuthTokenService,
    private readonly router: Router
  ) {}

  initialize(): void {
    if (this.initialized || this.loadingSignal()) {
      return;
    }
    this.initialized = true;
    this.loadAuthContext().subscribe();
  }

  refresh(): void {
    this.initialized = true;
    this.loadAuthContext().subscribe();
  }

  ensureAuthenticated(): Observable<boolean | UrlTree> {
    if (this.userSignal()) {
      return of(true);
    }

    return this.loadAuthContext().pipe(
      map((success) => (success ? true : this.router.parseUrl('/unauthorized')))
    );
  }

  login(): void {
    void this.authTokenService.login();
  }

  resetPassword(): void {
    void this.authTokenService.resetPassword();
  }

  logout(): void {
    void this.authTokenService.logout();
  }

  private loadAuthContext(): Observable<boolean> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    // Call registerLogin before getLoggedInUser so the CRM Contact gets linked to the B2C
    // objectId on first login and last-login is stamped on every login (bug 19263). Sequencing
    // matters: getLoggedInUser looks up by Azure ID, which only exists after registerLogin
    // writes it. Errors are swallowed so an already-linked user can still load the portal if
    // the linkage call fails; getLoggedInUser then surfaces 404s as it did before.
    return this.userService.registerLogin().pipe(
      catchError(() => of(null)),
      switchMap(() => this.userService.getLoggedInUser()),
      switchMap((user) =>
        this.userService.getUserClaims().pipe(
          map((claims) => {
            this.userSignal.set(user);
            this.claimsSignal.set(claims);
            this.locationStateService.setAllLocations(user.locationPermissionList ?? []);
            this.loadingSignal.set(false);
            return true;
          })
        )
      ),
      catchError(() => {
        this.userSignal.set(null);
        this.claimsSignal.set(null);
        this.locationStateService.setAllLocations([]);
        this.errorSignal.set('Unable to load authentication context.');
        this.loadingSignal.set(false);
        return of(false);
      })
    );
  }
}
